<template>
  <div>
    <!-- MODO “TELA NORMAL” -->
    <div v-if="!printMode">
      <h6>Retornos x Renovações</h6>
      <p></p>
      <div class="row mb-3">
        <div class="col-auto">
          <label for="mes_ref">Mês de referência:</label>
          <input
            id="mes_ref"
            type="month"
            class="form-control"
            v-model="mesAnoLocal"
          />
        </div>
        <div class="col-auto align-self-end">
          <button class="btn btn-primary" @click="abrirPrint">
            Executar
          </button>
        </div>
      </div>
    </div>

    <!-- MODO “IMPRESSÃO / PRINT” -->
    <div v-else class="relatorio-body" :data-user="props.userName">
      <div v-if="loading">Carregando…</div>

      <div v-else-if="dadosCarregados">
        <!-- Mês no topo, como no PDF -->
        <p class="text-center" style="font-size: 14px;">
          Mês: {{ nomeMes }} / {{ ano }}
        </p>

        <!-- RETORNOS -->
        <div class="mt-3">
            <h6>RETORNOS</h6>

            <table
                v-if="retornos.length"
                class="table table-striped"
            >
                <thead>
                <tr>
                    <th style="width: 5%;">#</th>
                    <th style="width: 15%;">Matrícula</th>
                    <th style="width: 40%;">Aluno</th>
                    <th style="width: 20%;">Fim (anterior)</th>
                    <th style="width: 20%;">Renovação em</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, idx) in retornos" :key="'ret-' + idx">
                    <td>{{ idx + 1 }}</td>
                    <td>{{ item.matricula_id }}</td>
                    <td>{{ item.aluno }}</td>
                    <td>{{ formatarData(item.fim_anterior) }}</td>
                    <td>{{ formatarData(item.renovacao_em) }}</td>
                </tr>
                </tbody>
            </table>

            <p v-else class="text-muted" style="font-size: 12px;">
                Nenhum retorno encontrado para o mês selecionado.
            </p>

            <p style="font-size: 11px; font-style: italic;">
                *Retornos são matrículas "retomadas" após o fim da vigência do plano anterior no mês do filtro.
            </p>
        </div>

        <!-- RENOVAÇÕES -->
        <div class="mt-4">
            <h6>RENOVAÇÕES</h6>

            <table
                v-if="renovacoes.length"
                class="table table-striped"
            >
                <thead>
                <tr>
                    <th style="width: 5%;">#</th>
                    <th style="width: 15%;">Matrícula</th>
                    <th style="width: 40%;">Aluno</th>
                    <th style="width: 20%;">Fim (anterior)</th>
                    <th style="width: 20%;">Renovação em</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, idx) in renovacoes" :key="'ren-' + idx">
                    <td>{{ idx + 1 }}</td>
                    <td>{{ item.matricula_id }}</td>
                    <td>{{ item.aluno }}</td>
                    <td>{{ formatarData(item.fim_anterior) }}</td>
                    <td>{{ formatarData(item.renovacao_em) }}</td>
                </tr>
                </tbody>
            </table>

            <p v-else class="text-muted" style="font-size: 12px;">
                Nenhuma renovação encontrada para o mês selecionado.
            </p>

            <p style="font-size: 11px; font-style: italic;">
                ** As renovações representam renovações simples e antecipadas registradas no mês do filtro.
            </p>
        </div>
      </div>

      <div v-else>
        <p class="text-muted">Nenhum dado encontrado.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from '@/services/axios'
import globalUtils from '@/utils/globalUtils.js'

const props = defineProps({
  printMode:  { type: Boolean, default: false },
  printTitle: { type: String, default: 'Relatório' },
  userName:   { type: String, default: '' }
})

const router = useRouter()
const route  = useRoute()

// Modo tela normal
const mesAnoLocal = ref('') // formato "YYYY-MM"

// Modo print
const loading       = ref(false)
const ano           = ref(null)
const mes           = ref(null)
const retornos      = ref([])
const renovacoes    = ref([])
const dadosCarregados = computed(() => ano.value && mes.value)

// Nomes dos meses (para exibir "Setembro / 2025")
const mesesNomes = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
]

const nomeMes = computed(() => {
  if (!mes.value) return ''
  return mesesNomes[mes.value - 1] || ''
})

async function carregarDados (anoParam, mesParam) {
  loading.value = true
  try {
    const res = await axios.get('/gestao/relatorios/operacionais/retornos-renovacoes', {
      params: { ano: anoParam, mes: mesParam }
    })
    ano.value        = res.data.ano
    mes.value        = res.data.mes
    retornos.value   = Array.isArray(res.data.retornos)   ? res.data.retornos   : []
    renovacoes.value = Array.isArray(res.data.renovacoes) ? res.data.renovacoes : []
  } catch (e) {
    console.error('Erro em RetornosRenovacoes (print):', e)
    retornos.value   = []
    renovacoes.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  if (props.printMode) {
    const anoQ = parseInt(route.query.ano, 10) || new Date().getFullYear()
    const mesQ = parseInt(route.query.mes, 10) || (new Date().getMonth() + 1)
    carregarDados(anoQ, mesQ)
  }
})

function abrirPrint () {
  if (!mesAnoLocal.value) return

  const [anoStr, mesStr] = mesAnoLocal.value.split('-')
  const anoNum = parseInt(anoStr, 10)
  const mesNum = parseInt(mesStr, 10)

  const mesNome = mesesNomes[mesNum - 1] || ''
  const subtitle = `Matrículas renovadas e retornos - Mês: ${mesNome} / ${anoNum}`

  router.push({
    path: '/gestao/relatorios/print/retornos-renovacoes',
    query: {
      title:    'RETORNOS X RENOVAÇÕES',
      subtitle: subtitle,
      ano:      anoNum,
      mes:      mesNum
    }
  })
}

function formatarData (strData) {
  if (!strData) return ''
  return globalUtils.formatarData(strData)
}
</script>

<style scoped>
.mb-3 {
  margin-bottom: 1rem;
}
.mt-3 {
  margin-top: 1rem;
}
.mt-4 {
  margin-top: 1.5rem;
}
.text-muted {
  font-size: 12px;
}
</style>
